import { Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { FormControl, UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { Store } from '@ngxs/store';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';
import { TranslationState } from 'src/app/shared/state/TranslationState';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { ColumnPreferencesService } from '../../../../shared/services/column-preferences.service';
import { ColumnPreferencesHelperService } from '../../../../shared/services/column-preferences-helper.service';
import { ROLES } from '../../../authentication/authRoles.constants';
import { Plant } from '../../../plant-setup/model/Plant';
import { PaginatedPlantLineResponse } from '../../../plant-setup/model/plantpagination.model';
import { CustomerFilters } from '../../models/customer-filters.model';
import { CustomerTableDetails } from '../../models/CustomerTableDetails';
import { CustomerSetupResponse, PaginatedCustomerLineResponse, Pagination } from '../../models/response.model';
import { AssignPlantsRequest, CustomerService } from '../../services/customer.service';

@Component({
  selector: 'app-customer-screen',
  templateUrl: './customer-screen.component.html',
  styleUrls: ['./customer-screen.component.scss', '../../customer-shared.scss'],
  standalone: false
})
export class CustomerScreenComponent implements OnInit {
  public translationService = inject(TranslationService);
  public customerService = inject(CustomerService);
  public alertService = inject(AlertService);
  public store = inject(Store);
  public router = inject(Router);
  public formBuilder = inject(UntypedFormBuilder);
  public route = inject(ActivatedRoute);
  public columnPrefService = inject(ColumnPreferencesService);
  public columnPrefsHelper = inject(ColumnPreferencesHelperService);
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Element>;
  @ViewChild('confirmation', { static: true }) changeConfirmModal: ModalComponent;
  @ViewChild('cancelConfirmation', { static: true }) unsavedAssignPlantChanges: ModalComponent;
  @ViewChild('viewAssignplant', { static: true }) viewAssignedplant: ModalComponent;
  @ViewChild('plantassignchangeContent') plantassignchangeContent: TemplateRef<any>;
  @ViewChild('confirmationContent') confirmationContent: TemplateRef<any>;
  @ViewChild('cancelConfirmationContent') cancelConfirmationContent: TemplateRef<any>;
  @ViewChild('assignedtoplants') assignedtoplantContent: TemplateRef<any>;

  customers: CustomerTableDetails[] = [];
  plants: Plant[] = [];
  currentDealer: string;
  search = new FormControl<string>('');
  pagination: Pagination;
  selection: CustomerSetupResponse[] = [];
  pageSizeOptions = [1, 5, 10, 50, 100];
  currentPage = 1;
  pageSize = 10;
  plantPageSize = 5;
  customerName = new FormControl<string>('');
  customerDescription = new FormControl<string>('');
  currentCustomer: CustomerTableDetails;
  isCustomerSelected: boolean;
  isPlantAssigned: boolean;
  assignPlantMode = false;
  selectedCustomers: string[];
  selectedPlants: string[];
  canSyncLabel = false;
  customerTypes = this.getCustomerTypes();
  columnVisibility: Record<string, boolean> = { shipTo: false, billTo: false, plantCodes: false };

  columnNames: Record<string, string> = {};
  private canonicalColumns: Record<string, any> = {};
  columnKeys: string[] = [];
  columnOrder: string[] = [];

  tableConfig: TableConfig<CustomerTableDetails> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom',
      pageLabelOptions: {
        firstPageLabel: '',
        lastPageLabel: '',
        nextPageLabel: '',
        previousPageLabel: '',
        itemsPerPageLabel: this.translationService.translate('pageLabelOptionText')
      }
    },
    onSelectionChange: (elms: CustomerTableDetails[]) => {
      // Log selection and update flag in separate statements (avoid comma operator triggering no-unused-expressions)
      console.log('Selection change', elms);
      this.isCustomerSelected = elms.length > 0;
      // Build selected customer list
      this.selectedCustomers = elms.map((elm) => elm.customerPk);
    },
    onRowClick: (elm: CustomerTableDetails) => {
      this.router.navigateByUrl('/home/customers/' + elm.customerPk);
    },
    selection: {
      columnKeyForCheckboxLabel: 'name',
      allLabel: 'All'
    },
    onColumnsChange: (columns: string[]) => console.log('Column display change', columns),
    columns: {}
  };

  tableConfig2: TableConfig<Plant> = {
    paginator: {
      pageSize: this.plantPageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    onSelectionChange: (elms: Plant[]) => {
      console.log('Selection change', elms);
      this.isPlantAssigned = elms.length > 0;
      this.selectedPlants = [];
      elms.forEach((elm) => {
        this.selectedPlants.push(elm.id);
      });
    },
    onRowClick: (elm: Plant) => {
      console.log('Row clicked', elm);
    },
    selection: {
      columnKeyForCheckboxLabel: 'name',
      allLabel: 'All'
    },
    onColumnsChange: (columns: string[]) => console.log('Column display change', columns),
    columns: {
      code: {
        cell: (elm: Plant) => elm.code,
        header: this.translationService.translate('plantCodeText'),
        sortable: true
      },
      name: {
        cell: (elm: Plant) => elm.name,
        header: this.translationService.translate('plantNameText'),
        sortable: false
      }
    }
  };

  state = TableConfig.getState(this.tableConfig, this.route);
  form = this.formBuilder.group({
    searchString: [this.state.extraFilters?.searchString],
    customerType: [this.state.extraFilters?.customerType],
    status: [this.state.extraFilters?.status || 'Y'],
    btmFlag: [this.state.extraFilters?.btmFlag]
  });

  fetcher: PaginatorFetcher<CustomerTableDetails> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    const customerFilters: CustomerFilters = this.form.value as CustomerFilters;

    return this.customerService.getCustomerLines(customerFilters, filters.page, filters.limit).pipe(
      map((customerResponse: PaginatedCustomerLineResponse) => {
        if (customerResponse != null && customerResponse.error != null && customerResponse.error.length > 0) {
          this.alertService.showAlert({
            type: AlertType.strong,
            content: 'Please try again. Something went wrong.'
          } as Alert);
          return;
        } else {
          this.customers = customerResponse.data.customers;
          this.pagination = customerResponse.data.pagination;
          return {
            items: this.customers,
            count: this.pagination.totalRecords
          };
        }
      })
    );
  };

  plantTableFetcher: PaginatorFetcher<Plant> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.plantPageSize = filters.limit;
    return this.customerService.getPlantList(filters.page, filters.limit).pipe(
      map((plantResponse: PaginatedPlantLineResponse) => {
        if (plantResponse != null && plantResponse.error != null && plantResponse.error.length > 0) {
          this.alertService.showAlert({
            type: AlertType.strong,
            content: 'Please try again. Something went wrong.'
          } as Alert);
          return;
        } else {
          this.plants = plantResponse.data.plants;
          this.pagination = plantResponse.data.pagination;
          return {
            items: this.plants,
            count: this.pagination.totalRecords
          };
        }
      })
    );
  };

  constructor() {}

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(
        debounceTime(1000),
        map((formValue) => ({
          ...formValue,
          searchString: formValue.searchString?.trim().toLowerCase() || ''
        })),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      )
      .subscribe((formValue) => {
        if (formValue.searchString.length === 0) {
          this.form.patchValue({ searchString: null }, { emitEvent: false });
          this.tableRef.patchExtraFilters({ ...formValue, searchString: null }, true);
          return;
        }

        if (formValue.searchString.length >= 2) {
          this.tableRef.patchExtraFilters(formValue, true);
        }
      });

    this.tableConfig.onColumnsChange = (columns: string[]) => {
      this.columnVisibility = {
        shipTo: columns.includes('shipTo'),
        billTo: columns.includes('billTo')
      };
    };

    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.customerTypes = this.getCustomerTypes();

      this.canonicalColumns = {
        code: {
          cell: (elm: CustomerTableDetails) => elm.code,
          header: this.translationService.translate('customerCodeText')
        },
        name: {
          cell: (elm: CustomerTableDetails) => elm.customerName,
          header: this.translationService.translate('customerNameText')
        },
        city: {
          cell: (elm: CustomerTableDetails) => elm.city,
          header: this.translationService.translate('cityText')
        },
        state: {
          cell: (elm: CustomerTableDetails) => elm.stateProvince,
          header: this.translationService.translate('customerStateProvinceText')
        },
        camSpecName: {
          cell: (elm: CustomerTableDetails) => elm.camSpecName,
          header: this.translationService.translate('camSpecNameColumnText')
        },
        btm: {
          cell: (elm: CustomerTableDetails) => elm.btmFlag,
          header: this.translationService.translate('btmFlagText')
        },
        type: { cell: 'typeCell', header: this.translationService.translate('typeColumnText') },
        status: { cell: 'activeCell', header: this.translationService.translate('statusColumnText') },
        plantCodes: { cell: 'plantCell', header: this.translationService.translate('plantAssignmentColumnText') },
        shipTo: {
          cell: (elm: CustomerTableDetails) => elm.michelinShipTo,
          header: this.translationService.translate('michelinShipToText')
        },
        billTo: {
          cell: (elm: CustomerTableDetails) => elm.michelinBillTo,
          header: this.translationService.translate('michelinBillToText')
        }
      };

      this.columnNames = Object.keys(this.canonicalColumns).reduce((acc, k) => {
        acc[k] = this.canonicalColumns[k].header;
        return acc;
      }, {} as Record<string, string>);

      // initialize columns with user preferences (localStorage)
      const init = this.columnPrefsHelper.initialize('customer-screen', this.canonicalColumns);
      this.tableConfig.columns = init.tableColumns;
      this.columnNames = init.columnNames;
      this.columnKeys = init.columnKeys;
      this.columnOrder = init.columnOrder;
      this.columnVisibility = init.columnVisibility;

      this.tableConfig2.columns = {
        code: {
          cell: (elm: Plant) => elm.code,
          header: this.translationService.translate('plantCodeText'),
          sortable: true
        },
        name: {
          cell: (elm: Plant) => elm.name,
          header: this.translationService.translate('plantNameText'),
          sortable: false
        }
      };
    });

    if (localStorage.getItem('roles').includes(ROLES.btn_admin)) {
      this.canSyncLabel = true;
    }
  }

  // Toggle visibility for a column and persist preferences (delegates to helper)
  toggleColumn(key: string): void {
    const result = this.columnPrefsHelper.toggleAndSave(
      'customer-screen',
      key,
      this.canonicalColumns,
      this.columnOrder,
      this.columnVisibility
    );
    this.tableConfig.columns = result.tableColumns;
    this.columnVisibility = result.columnVisibility;
  }

  resetFilters(): void {
    this.form.reset();
  }

  clearSearch() {
    this.search.setValue('');
  }
  showAssignPlantModel() {
    this.isPlantAssigned = false;
    this.viewAssignedplant.open({
      id: 'assignedtoplant',
      width: 950,
      title: this.translationService.translate('selectPlantText'),
      content: this.assignedtoplantContent
    });
  }
  showConfirmChangeModal() {
    this.changeConfirmModal.open({
      id: 'changeConfirmModal',
      width: 550,
      title: this.translationService.translate('confirmChangesText'),
      content: this.confirmationContent,
      confirm: {
        text: this.translationService.translate('continueButtonText'),
        color: 'primary'
      },
      cancel: this.translationService.translate('cancelLinkText')
    });
  }

  cancelAssignPlantsModal() {
    this.viewAssignedplant.dialog.closeAll();
  }

  showUnsavedAssignPlantsChangesModal() {
    this.unsavedAssignPlantChanges.open({
      id: 'unsavedAssignPlantChanges',
      width: 550,
      title: this.translationService.translate('discardChangesText'),
      content: this.cancelConfirmationContent,
      confirm: {
        text: this.translationService.translate('discardChangesText'),
        color: 'primary'
      },
      cancel: this.translationService.translate('cancelLinkText')
    });
  }
  onAssignPlantConfirmationModalClosed(result: ModalResult) {
    if (result.result) {
      this.assignPlantMode = true;
      let payload: AssignPlantsRequest = {
        customers: this.selectedCustomers,
        plants: this.selectedPlants
      };
      this.customerService.assignPlantToCustomer(payload).subscribe(
        (v) => {
          if (v) {
            this.alertService.showAlert({
              type: AlertType.success,
              content: this.translationService.translate('plantAssignmentSuccessText')
            } as Alert);
          }
          this.assignPlantMode = false;
        },
        (error) => {
          const statusCode: number = error.error.statusCode;
          const alertType = 400 <= statusCode && statusCode < 500 ? AlertType.strong : AlertType.critical;
          this.alertService.showAlert({
            type: alertType,
            title: 'Critical Error',
            content: 'Please try again. Something went wrong.'
          } as Alert);
        }
      );
      this.tableRef.resetFilters();
      this.cancelAssignPlantsModal();
    }
  }

  onUnsavedAssignPlantsChangesModalClosed(result: ModalResult) {
    if (result.result) {
      this.cancelAssignPlantsModal();
      this.ngOnInit();
    }
  }

  createCustomer() {
    this.router.navigateByUrl('/home/customers/create');
  }

  syncLabels() {
    this.customerService.syncLabels().subscribe({
      next: () => {
        this.alertService.showAlert({
          type: AlertType.success,
          content: 'Labels Synced Successfully'
        } as Alert);
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.strong,
          content: 'Please try again. Something went wrong.'
        } as Alert);
      }
    });
  }

  private getCustomerTypes(): { key: string; value: string }[] {
    return [
      { key: 'Customer', value: this.translationService.translate('customerText') },
      { key: 'AssociateDealer', value: this.translationService.translate('associateDealerText') },
      { key: 'AssociateCustomer', value: this.translationService.translate('associateDealerCustomerText') },
      { key: 'DefaultCustomer', value: this.translationService.translate('defaultCustomerText') }
    ];
  }
}
